import { formatISODate, formatTime } from "@clipboard-health/date-time";
import { Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { ScrollbarWrapper } from "@src/appV2/redesign/components/ScrollbarWrapper";
import { SelectableBox } from "@src/appV2/redesign/components/SelectableBox";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format } from "date-fns";
import { useEffect, useRef } from "react";

import { type PlacementDetail } from "../api/useGetPlacement";

export interface InterviewTime {
  start: Date;
  end: Date;
  isBooked?: boolean;
}

interface InterviewTimeSelectorProps {
  isSelectionPromoted: boolean;
  placementDetails: PlacementDetail;
  timeSlots: InterviewTime[];
  selectedTime: InterviewTime | undefined;
  onTimeSelect: (time: InterviewTime) => void;
  isReschedule?: boolean;
  shouldScrollToSelected?: boolean;
}

export function InterviewTimeSelector(props: InterviewTimeSelectorProps) {
  const {
    isSelectionPromoted,
    placementDetails,
    timeSlots,
    selectedTime,
    onTimeSelect,
    isReschedule = false,
    shouldScrollToSelected = false,
  } = props;

  const timeSlotRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const hasScrolledRef = useRef(false);

  const isProspectWorkplace = Boolean(placementDetails.workplace.placementSettings?.isProspect);

  const eventContext = {
    placementId: placementDetails.id,
    placementApplicationId: placementDetails.applications?.[0]?.id,
    isReschedule,
    isProspectWorkplace,
  };

  const handleTimeSelect = (time: InterviewTime) => {
    onTimeSelect(time);

    // Track time selection
    logEvent(APP_V2_APP_EVENTS.INTERVIEW_TIME_SELECTED, {
      ...eventContext,
      selectedDate: formatISODate(time.start),
      selectedTimeRange: `${formatTime(time.start)} - ${formatTime(time.end)}`,
    });
  };

  // Scroll to selected time when it's restored from localStorage
  useEffect(() => {
    if (
      !shouldScrollToSelected ||
      !selectedTime ||
      hasScrolledRef.current ||
      timeSlots.length === 0
    ) {
      return;
    }

    const timeKey = selectedTime.start.toISOString();
    const selectedElement = timeSlotRefs.current.get(timeKey);

    if (selectedElement) {
      // Use setTimeout to ensure the DOM is fully rendered
      setTimeout(() => {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        hasScrolledRef.current = true;
      }, 100);
    }
  }, [shouldScrollToSelected, selectedTime, timeSlots.length]);

  // Reset scroll flag when selectedTime changes (user selects a different time)
  useEffect(() => {
    if (!shouldScrollToSelected) {
      hasScrolledRef.current = false;
    }
  }, [selectedTime, shouldScrollToSelected]);

  return (
    <Stack spacing={1} sx={{ py: 2 }}>
      <Text variant="body1" sx={{ py: 1 }}>
        Available time slots
      </Text>
      <ScrollbarWrapper height="280px" sx={{ p: 2, gap: 3 }} showScrollbarTrack={false}>
        {timeSlots.map((time) => {
          const timeKey = time.start.toISOString();

          if (time.isBooked) {
            return (
              <SelectableBox
                key={`booked-${timeKey}`}
                variant="secondary"
                sx={{ opacity: 0.5 }}
                disabled={true}
                onToggleSelect={() => {}} // noop
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: "100%" }}
                >
                  <Text
                    variant="body2"
                    sx={{
                      textDecoration: "line-through",
                      color: (theme) => theme.palette.text.tertiary,
                    }}
                  >
                    {formatTime(time.start)} - {formatTime(time.end)}
                  </Text>
                  <Text variant="caption" sx={{ color: (theme) => theme.palette.text.tertiary }}>
                    Booked
                  </Text>
                </Stack>
              </SelectableBox>
            );
          }

          const isSelected = selectedTime?.start.getTime() === time.start.getTime();

          return (
            <SelectableBox
              key={timeKey}
              variant="secondary"
              sx={{
                ...(isSelectionPromoted && {
                  background: `linear-gradient(90.67deg, #FAECDE 5.65%, #D7D4FF 94.45%)`,
                }),
              }}
              isSelected={isSelected}
              onToggleSelect={() => {
                handleTimeSelect(time);
              }}
            >
              <Box
                ref={(element: HTMLDivElement | null) => {
                  if (element) {
                    timeSlotRefs.current.set(timeKey, element);
                  } else {
                    timeSlotRefs.current.delete(timeKey);
                  }
                }}
              >
                {format(time.start, "h:mm aaa")} - {format(time.end, "h:mm aaa")}
              </Box>
            </SelectableBox>
          );
        })}
      </ScrollbarWrapper>
    </Stack>
  );
}
